/**
 * Axis unit conversion for sport-context-aware charts.
 *
 * Every unit is described by one row in {@link AXIS_UNIT_DEFS} — its label, its
 * domain, how to convert from the base unit, and how to format it. Adding a new
 * reference system means adding one row; nothing else in this file changes.
 *
 * Two facts drive the conversion:
 *  1. Raw axis values arrive in the sport's NATIVE base unit — watts for power
 *     sports (simulation_type = POWER), metres-per-second for speed sports
 *     (simulation_type = SPEED).
 *  2. The display unit may be in the SAME domain (pure-math conversion) or the
 *     OPPOSITE domain (cross-domain lookup through the index-aligned
 *     power[]/vms[] sample arrays from the test dataframe).
 *
 * Mirrors the legacy `calculateTicker`/`convertUnit` pipeline (assets/js/
 * chart-global-service.js), minus its `value > 10` magnitude heuristic — the
 * domain is passed explicitly here, so that hack (which misfires for low-power
 * or high-speed samples) is gone.
 */

export const enum AxisReferenceSystem {
  POWER_WATT        = 1,
  POWER_WATT_PER_KG = 2,
  SPEED_MS          = 3,
  SPEED_KM_H        = 4,
  SPEED_MPH         = 5,
  PACE_MIN_KM       = 6,
  PACE_MIN_MILE     = 7,
  PACE_MIN_100M     = 8,
  PACE_MIN_500M     = 9,
}

export const enum SportCategory {
  SPEED = 1,
  POWER = 2,
}

export interface AxisUnitDef {
  label: string;
  category: SportCategory;
  /** Convert the domain base unit (watts for POWER, m/s for SPEED) to this unit. */
  fromBase: (base: number, mass?: number) => number;
  /** Format the converted value for axis/legend display. */
  format: (value: number) => string;
  /** Inverse of {@link fromBase} — convert this unit's numeric value back to the domain base unit. */
  toBase: (value: number, mass?: number) => number;
  /**
   * Which control an editable field for this unit should use. `'pace'` fields hold a
   * "m:ss.s" string (parsed via {@link parsePace}, formatted via {@link format}); all
   * others are plain decimal numbers.
   */
  inputKind: 'number' | 'pace';
}

const KM_PER_MILE = 1.609344;

// Composable conversion/format helpers — keep the table below declarative.
const identity = (base: number) => base;
const fixed = (digits: number) => (value: number) => value.toFixed(digits);
const integer = (value: number) => Math.round(value).toString();

/** speed (m/s) → minutes per `metres` (decimal), for pace units. */
const paceOver = (metres: number) => (base: number) => (base > 0 ? metres / base / 60 : 0);
/** decimal minutes per `metres` → speed (m/s) — inverse of {@link paceOver}. */
const paceToBase = (metres: number) => (minutes: number) =>
  minutes > 0 ? metres / (minutes * 60) : 0;
/** decimal minutes → "m:ss.s" — one decimal place on the seconds (IAT-2593). */
export const formatPace = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—';
  const totalSec = Math.round(minutes * 60 * 10) / 10;
  let wholeMin = Math.floor(totalSec / 60);
  let sec = totalSec - wholeMin * 60;
  if (sec >= 60) {
    sec -= 60;
    wholeMin += 1;
  }
  return `${wholeMin}:${sec.toFixed(1).padStart(4, '0')}`;
};

/**
 * "m:ss.s" (or "m:ss") → decimal minutes — inverse of {@link formatPace}. Returns `null`
 * for unparseable input (seconds must be 00-59).
 */
export function parsePace(text: string): number | null {
  const match = /^(\d{1,3}):([0-5]\d)(?:\.(\d))?$/.exec(text.trim());
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]) + (match[3] ? Number(match[3]) / 10 : 0);
  return minutes + seconds / 60;
}

const POWER = SportCategory.POWER;
const SPEED = SportCategory.SPEED;

/** Single source of truth: one row per reference system. */
export const AXIS_UNIT_DEFS: Readonly<Record<AxisReferenceSystem, AxisUnitDef>> = {
  [AxisReferenceSystem.POWER_WATT]:        { label: 'watt',         category: POWER, fromBase: identity,                       format: integer,    toBase: identity,                             inputKind: 'number' },
  [AxisReferenceSystem.POWER_WATT_PER_KG]: { label: 'watt/kg',      category: POWER, fromBase: (b, m) => (m ? b / m : b),     format: fixed(2),   toBase: (v, m) => (m ? v * m : v),           inputKind: 'number' },
  [AxisReferenceSystem.SPEED_MS]:          { label: 'm/s',          category: SPEED, fromBase: identity,                       format: fixed(2),   toBase: identity,                             inputKind: 'number' },
  [AxisReferenceSystem.SPEED_KM_H]:        { label: 'km/h',         category: SPEED, fromBase: (b) => b * 3.6,                 format: fixed(1),   toBase: (v) => v / 3.6,                      inputKind: 'number' },
  [AxisReferenceSystem.SPEED_MPH]:         { label: 'mph',          category: SPEED, fromBase: (b) => (b * 3.6) / KM_PER_MILE, format: fixed(1),   toBase: (v) => (v * KM_PER_MILE) / 3.6,      inputKind: 'number' },
  [AxisReferenceSystem.PACE_MIN_KM]:       { label: 'min:sec/km',   category: SPEED, fromBase: paceOver(1000),                 format: formatPace, toBase: paceToBase(1000),                    inputKind: 'pace' },
  [AxisReferenceSystem.PACE_MIN_MILE]:     { label: 'min:sec/mile', category: SPEED, fromBase: paceOver(1000 * KM_PER_MILE),   format: formatPace, toBase: paceToBase(1000 * KM_PER_MILE),      inputKind: 'pace' },
  [AxisReferenceSystem.PACE_MIN_100M]:     { label: 'min:sec/100m', category: SPEED, fromBase: paceOver(100),                  format: formatPace, toBase: paceToBase(100),                     inputKind: 'pace' },
  [AxisReferenceSystem.PACE_MIN_500M]:     { label: 'min:sec/500m', category: SPEED, fromBase: paceOver(500),                  format: formatPace, toBase: paceToBase(500),                     inputKind: 'pace' },
};

/** A selectable reference system for configuration UIs. */
export interface ReferenceSystemOption {
  id: AxisReferenceSystem;
  /** Display unit, e.g. "min:sec/km". */
  unit: string;
  /** Power- vs speed-domain — used to flag a mismatch against the sport. */
  category: SportCategory;
}

/** All reference systems in id order, derived from {@link AXIS_UNIT_DEFS}. */
export const REFERENCE_SYSTEM_OPTIONS: readonly ReferenceSystemOption[] = (
  Object.entries(AXIS_UNIT_DEFS) as [string, AxisUnitDef][]
).map(([id, def]) => ({ id: Number(id) as AxisReferenceSystem, unit: def.label, category: def.category }));

// Representative base values used purely to illustrate a unit's format/scale.
const EXAMPLE_POWER_WATTS = 250;
const EXAMPLE_SPEED_MS = 4;
const EXAMPLE_MASS_KG = 70;

/**
 * A representative formatted value for a reference system, e.g. "4:10 min:sec/km" or
 * "250 watt" — shown as a hint so the user can see what the chosen unit looks like.
 */
export function referenceSystemExample(
  system: AxisReferenceSystem,
  mass: number = EXAMPLE_MASS_KG,
): string {
  const def = AXIS_UNIT_DEFS[system];
  const base = def.category === POWER ? EXAMPLE_POWER_WATTS : EXAMPLE_SPEED_MS;

  return `${def.format(def.fromBase(base, mass || EXAMPLE_MASS_KG))} ${def.label}`;
}

/**
 * Index-aligned native-domain sample arrays from the test dataframe
 * (`data_df_power` / `data_df_vms`). Required only for cross-domain
 * conversion — e.g. displaying watts on a speed-based test.
 */
export interface AxisDomainLookup {
  power: number[]; // watts
  vms: number[]; // m/s
}

/** Everything needed to convert and format a raw axis tick for display. */
export interface AxisTickContext {
  /** Target display unit. */
  system: AxisReferenceSystem;
  /** Native domain of the raw axis values (watts vs m/s). */
  simulationType: SportCategory;
  /** Athlete body mass in kg — required only for {@link AxisReferenceSystem.POWER_WATT_PER_KG}. */
  mass?: number;
  /** Paired sample arrays — required only when target domain ≠ native domain. */
  lookup?: AxisDomainLookup;
  /**
   * The sport's optional second display unit. When present, {@link xAxisUnitAxes} emits a
   * second x axis in this unit; absent means the sport has no secondary system configured.
   */
  secondarySystem?: AxisReferenceSystem;
}

/**
 * Resolve the reference system for a sport.
 * Uses `primaryType` (backend `sport.primary_type`) when present and valid,
 * then falls back to the canonical base unit for the sport category.
 */
export function resolveSportReferenceSystem(
  primaryType: number | undefined,
  simulationType: number | undefined,
): AxisReferenceSystem {
  if (primaryType !== undefined && primaryType in AXIS_UNIT_DEFS) {
    return primaryType as AxisReferenceSystem;
  }
  return simulationType === POWER ? AxisReferenceSystem.POWER_WATT : AxisReferenceSystem.SPEED_MS;
}

/**
 * Resolve an optional (secondary) reference system. Unlike {@link resolveSportReferenceSystem}
 * there is no sensible fallback — `null` means "not configured" (stored as `0`/absent on the
 * backend), which callers should treat as "no secondary unit available".
 */
export function resolveOptionalReferenceSystem(
  type: number | null | undefined,
): AxisReferenceSystem | null {
  return type != null && type in AXIS_UNIT_DEFS ? (type as AxisReferenceSystem) : null;
}

/**
 * Convert a raw axis value to the display unit's numeric value.
 * Returns `null` when a cross-domain lookup is required but unavailable.
 */
export function convertAxisValue(raw: number, ctx: AxisTickContext): number | null {
  const def = AXIS_UNIT_DEFS[ctx.system];
  const base = resolveBase(raw, def.category, ctx);
  return base == null ? null : def.fromBase(base, ctx.mass);
}

/**
 * Format a raw axis tick value as a display string.
 * Suitable as the return value of an ECharts `axisLabel.formatter`.
 */
export function formatAxisTick(raw: number, ctx: AxisTickContext): string {
  const value = convertAxisValue(raw, ctx);
  if (value == null || !Number.isFinite(value)) return '—';
  return AXIS_UNIT_DEFS[ctx.system].format(value);
}

/**
 * Reference systems whose 2-decimal default becomes 3 decimals for swimming sports
 * (IAT-2593) — pace systems are unaffected (always one decimal on the seconds), and
 * `POWER_WATT`/`SPEED_KM_H` are unaffected (they aren't 2-decimal to begin with).
 */
const SWIMMING_UPGRADE_SYSTEMS = new Set<AxisReferenceSystem>([
  AxisReferenceSystem.POWER_WATT_PER_KG,
  AxisReferenceSystem.SPEED_MS,
  AxisReferenceSystem.SPEED_MPH,
]);

/**
 * Converts a raw base value (watts for POWER, m/s for SPEED) to `system`'s display unit
 * and formats it, applying the swimming 2→3 decimal upgrade. Same-domain only — `system`
 * must already match the value's native domain (no cross-domain lookup, unlike
 * {@link convertAxisValue}). Used to reconstruct a representation's display value
 * client-side instead of trusting an API-baked string.
 */
export function formatSystemValue(
  system: AxisReferenceSystem,
  base: number,
  mass?: number,
  isSwimming = false,
): string {
  const def = AXIS_UNIT_DEFS[system];
  const converted = def.fromBase(base, mass);
  return isSwimming && SWIMMING_UPGRADE_SYSTEMS.has(system) ? converted.toFixed(3) : def.format(converted);
}

/**
 * Display unit label for a reference system; falls back to the API-supplied
 * label when the system is undefined. The reference system always wins —
 * matching the legacy axis, where `x_axis_label` is only a legend hint.
 */
export function axisUnitLabel(system: AxisReferenceSystem | undefined, apiLabel?: string): string {
  return system !== undefined && system in AXIS_UNIT_DEFS
    ? AXIS_UNIT_DEFS[system].label
    : (apiLabel ?? '');
}

/**
 * Build the shared props for an ECharts value x-axis: unit label, tick
 * formatter, and the data extremes as `min`/`max`.
 *
 * Pinning `min`/`max` to the first/last power sample (rather than `scale: true`)
 * keeps the plotted curves and their area fills flush with the left/right plot
 * edges — every load-characteristics chart shares the same x data range, so
 * they stay aligned, matching the legacy report layout. With `scale: true`
 * ECharts auto-extends the axis to round bounds beyond the data, leaving empty
 * gutters where the fills no longer reach the edge.
 */
export function xAxisUnitProps(
  apiLabel: string | undefined,
  ctx: AxisTickContext | undefined,
  power: readonly number[] = [],
  /** Explicit range (the backend's combined range on a comparison chart). Wins over `power`. */
  range?: [number, number],
): UnitXAxisProps {
  const min = range ? range[0] : power.length ? Math.min(...power) : undefined;
  const max = range ? range[1] : power.length ? Math.max(...power) : undefined;

  return {
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    name: axisUnitLabel(ctx?.system, apiLabel),
    ...(ctx && { axisLabel: { formatter: (v: number) => formatAxisTick(v, ctx) } }),
  };
}

/** Unit-aware props shared by every x axis {@link xAxisUnitAxes} builds. */
export interface UnitXAxisProps {
  min?: number;
  max?: number;
  name: string;
  axisLabel?: { formatter: (v: number) => string };
}

/** An ECharts value x-axis, ready to drop into `option.xAxis`. */
export interface UnitXAxis extends UnitXAxisProps {
  type: 'value';
  position?: 'bottom';
  offset?: number;
  splitLine?: { show: boolean };
}

/**
 * Vertical room (px) the primary x axis occupies below the plot — its ticks, its labels
 * and its centred name — and therefore how far the secondary axis has to be pushed down
 * to clear it. Derived from the base theme's `axisNameGapX` (28) plus the name's own
 * font size (12), rounded up for breathing room.
 */
const SECONDARY_X_AXIS_OFFSET = 44;

/**
 * Build `option.xAxis`: the primary unit axis, plus a second axis stacked beneath it when
 * the sport has a secondary reference system configured.
 *
 * Both axes span the *same* numeric range and differ only in their tick formatter, so a
 * point on the plot can be read off in either unit — this mirrors the legacy Bokeh
 * `addSecondaryXAxis`, which added two `LinearAxis` layouts 'below' against `primary` and
 * `secondary` ranges holding identical bounds.
 *
 * Series need no `xAxisIndex`: they bind to axis 0 by default. Charts that zoom must bind
 * their `dataZoom` to every returned axis (see `syncedXDataZoom`) or the two unit rows
 * drift apart.
 */
export function xAxisUnitAxes(
  apiLabel: string | undefined,
  ctx: AxisTickContext | undefined,
  power: readonly number[] = [],
  range?: [number, number],
): UnitXAxis[] {
  const primary: UnitXAxis = { type: 'value', ...xAxisUnitProps(apiLabel, ctx, power, range) };

  // Nothing to add when the sport has no secondary system, or when it duplicates the primary.
  if (!ctx?.secondarySystem || ctx.secondarySystem === ctx.system) return [primary];

  const secondaryCtx: AxisTickContext = { ...ctx, system: ctx.secondarySystem };

  return [
    primary,
    {
      ...primary,
      name: axisUnitLabel(ctx.secondarySystem),
      axisLabel: { formatter: (v: number) => formatAxisTick(v, secondaryCtx) },
      // Explicit: ECharts puts a second x axis on top by default, since the first one
      // already claimed 'bottom'. Both belong below the plot, stacked.
      position: 'bottom',
      offset: SECONDARY_X_AXIS_OFFSET,
      // One scale, two labellings — the primary already drew the vertical grid, and a
      // second set would land on exactly the same lines.
      splitLine: { show: false },
    },
  ];
}

/**
 * Resolve a raw axis value into the target domain's base unit (watts or m/s).
 * Same domain → the raw value is already in the base unit. Cross domain → map
 * through the paired sample arrays; `null` if they're missing a usable value.
 */
function resolveBase(
  raw: number,
  targetCategory: SportCategory,
  ctx: AxisTickContext,
): number | null {
  if (targetCategory === ctx.simulationType) return raw;
  // Cross-domain (e.g. watts on a speed test) needs the paired sample arrays.
  // Without them we cannot convert — return null rather than a wrong number.
  if (!ctx.lookup) return null;

  const nativeArr = ctx.simulationType === POWER ? ctx.lookup.power : ctx.lookup.vms;
  const targetArr = targetCategory === POWER ? ctx.lookup.power : ctx.lookup.vms;
  const paired = targetArr[closestIndex(nativeArr, raw)];
  return paired != null && Number.isFinite(paired) ? paired : null;
}

/** Index of the array element nearest to `target` (legacy `closest`). */
function closestIndex(list: number[], target: number): number {
  let best = 0;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (let i = 0; i < list.length; i++) {
    const diff = Math.abs(list[i] - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return best;
}
